import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { CategoryKey, ContributionTier, PriceRange, ReturnVerdict } from "./constants";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  console.warn(
    "[supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. The admin will run in offline UI mode only.",
  );
}

export const supabase: SupabaseClient = createClient(
  url ?? "https://offline.invalid",
  anonKey ?? "offline",
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  },
);

export const REVIEW_IMAGES_BUCKET =
  import.meta.env.VITE_REVIEW_IMAGES_BUCKET ?? "review-images";

export const AUTH_REDIRECT_URL =
  import.meta.env.VITE_AUTH_REDIRECT_URL ?? window.location.origin;

/**
 * Row shapes matching supabase/schema.sql.
 */

export interface ReviewRow {
  id: string;
  restaurant_name: string;
  address: string | null;
  city: string | null;
  neighborhood: string | null;
  cuisine: string | null;
  price_range: PriceRange | null;
  date_visited: string | null;
  what_ordered: string | null;
  visit_count: number;
  independent_paid_visit_completed: boolean;
  score_flavor: number | null;
  score_seasoning: number | null;
  score_texture: number | null;
  score_presentation: number | null;
  score_value: number | null;
  score_service: number | null;
  score_would_return: number | null;
  weights_version: string;
  final_score: number | null;
  paragraph_flavor: string | null;
  paragraph_seasoning: string | null;
  paragraph_texture: string | null;
  paragraph_presentation: string | null;
  paragraph_value: string | null;
  paragraph_service: string | null;
  verdict: string | null;
  return_verdict: ReturnVerdict | null;
  return_condition: string | null;
  reader_requested: boolean;
  contribution_received: boolean;
  comped: boolean;
  disclosure_text: string | null;
  published: boolean;
  slug: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReviewAssetRow {
  id: string;
  review_id: string;
  storage_path: string;
  caption: string | null;
  sort_order: number;
  is_primary: boolean;
  created_at: string;
}

export interface ContributionRow {
  id: string;
  review_request_id: string | null;
  review_id: string | null;
  amount_cents: number;
  refunded_cents: number;
  payment_processor: string | null;
  processor_reference: string | null;
  note: string | null;
  created_at: string;
}

export interface ReviewRequestRow {
  id: string;
  requester_name: string;
  requester_email: string;
  restaurant_name: string;
  address: string | null;
  reason: string | null;
  tier: ContributionTier;
  contribution_cents: number;
  consent_independence: boolean;
  consent_non_negotiable: boolean;
  consent_disclosure: boolean;
  status: "new" | "queued" | "scheduled" | "reviewed" | "rejected" | "pending_payment";
  payment_status: "none" | "pending" | "paid" | "failed";
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  notes: string | null;
  created_at: string;
}

export type ScoreColumn = `score_${CategoryKey}`;
export type ParagraphColumn =
  | "paragraph_flavor"
  | "paragraph_seasoning"
  | "paragraph_texture"
  | "paragraph_presentation"
  | "paragraph_value"
  | "paragraph_service";
